import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, User, MessageCircle } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { Reveal } from "@/components/common/Reveal";
import { useQuote } from "@/context/QuoteContext";
import client from "@/lib/api";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { openQuote } = useQuote();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    client.get(`/blog/${slug}`).then(({ data }) => setPost(data)).catch(() => setNotFound(true));
  }, [slug]);

  useSEO({
    title: post ? post.title : "Artículo",
    description: post ? post.excerpt : "",
    image: post?.cover_image,
    path: `/blog/${slug}`,
    jsonLd: post
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.excerpt,
          image: post.cover_image,
          author: { "@type": "Organization", name: post.author },
          publisher: { "@type": "Organization", name: "GEMA" },
        }
      : undefined,
  });

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-gema-black text-center px-6">
        <p className="text-white/50">Artículo no encontrado.</p>
        <button onClick={() => navigate("/blog")} className="btn-outline-light mt-6">Volver al blog</button>
      </div>
    );
  }

  if (!post) {
    return <div className="min-h-[60vh] flex items-center justify-center bg-gema-black text-white/40 font-mono text-sm">Cargando…</div>;
  }

  return (
    <article className="bg-gema-black">
      {/* Hero */}
      <div className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden">
        <img src={post.cover_image} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-25 grayscale" />
        <div className="absolute inset-0 bg-gradient-to-t from-gema-black via-gema-black/80 to-gema-black/50" />
        <div className="container-gema relative z-10 max-w-3xl">
          <Link to="/blog" className="inline-flex items-center gap-2 text-white/50 hover:text-gema-red transition-colors text-xs font-mono uppercase tracking-wider mb-8">
            <ArrowLeft className="h-3 w-3" /> Volver al blog
          </Link>
          <span className="inline-block font-mono text-[10px] uppercase tracking-[0.2em] text-white bg-gema-red px-3 py-1">{post.category}</span>
          <h1 className="heading-lg text-white mt-6">{post.title}</h1>
          <div className="flex flex-wrap items-center gap-6 mt-6 text-white/50 text-sm">
            <span className="flex items-center gap-2"><User className="h-4 w-4" /> {post.author}</span>
            <span className="flex items-center gap-2"><Clock className="h-4 w-4" /> {post.read_time}</span>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="container-gema max-w-3xl pb-24">
        <Reveal>
          <div className="prose-gema" data-testid="blog-content">
            {post.content.split("\n\n").map((para, i) => (
              <p key={i} className="text-white/70 leading-relaxed text-lg mb-6">{para}</p>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-16 border border-white/10 bg-gema-ink p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <h3 className="font-heading font-black uppercase text-xl text-white">¿Tienes un proyecto en mente?</h3>
              <p className="text-white/50 text-sm mt-2">Solicita una cotización sin compromiso.</p>
            </div>
            <button onClick={() => openQuote()} data-testid="blogpost-quote-btn" className="btn-primary shrink-0">
              <MessageCircle className="h-4 w-4" /> Solicitar Cotización
            </button>
          </div>
        </Reveal>
      </div>
    </article>
  );
}
